// packages/web/clinical/src/components/AuthenticationHandler.tsx
import React, { useEffect, useState } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { loginRequest, apiRequest } from '../../msalConfig';
import { Box } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import CustomTypography from '@ui-components/Typography/CustomTypography';
import CustomButton from '@ui-components/Button/CustomButton';
import './signin.css';
import { useGetUserDetails } from '../../API/hooks/loginHook';
import QLerLogo from '../../assets/images/qler_Logo.svg';
import { CustomCard } from '@qler-view/shared_lib/src/index.js';

export default function AuthenticationHandler({
  children,
}: {
  readonly children: React.ReactNode;
}) {
  const { instance, accounts, inProgress } = useMsal();
  const navigate = useNavigate();
  const location = useLocation();
  const [token, setToken] = useState<string | null>(null);
  const [userDetailsReady, setUserDetailsReady] = useState(false);
  const isAuthenticated = useIsAuthenticated();

  const username = accounts[0]?.username;
  const { userDetails, isError, refetch } = useGetUserDetails(username);
  const [redirected, setRedirected] = useState(false);

  const isAccessDenied = location.pathname === '/access-denied';

  useEffect(() => {
    if (accounts.length > 0) {
      instance.setActiveAccount(accounts[0]);
    }
  }, [accounts, instance]);

  useEffect(() => {
    const acquireToken = async () => {
      if (!isAuthenticated || inProgress !== 'none') return;

      const account = instance.getActiveAccount();
      if (!account) return;

      try {
        const response = await instance.acquireTokenSilent({
          ...apiRequest,
          account,
        });

        setToken(response.accessToken);
      } catch (err) {
        if (err instanceof InteractionRequiredAuthError) {
          instance.acquireTokenRedirect(apiRequest);
        } else {
          console.error('Token acquisition failed:', err);
        }
      }
    };

    acquireToken();
  }, [isAuthenticated, inProgress, instance]);

  useEffect(() => {
    if (!isAuthenticated || accounts.length === 0) return;

    const roles = (accounts[0].idTokenClaims?.roles as string[]) || [];

    const allowed = roles.includes('Provider');

    if (!allowed) {
      console.error('Blocked by Entra role', roles);

      // Send user to denied page
      navigate('/access-denied', { replace: true });
      history.replaceState(null, '', '/access-denied');
    }
  }, [isAuthenticated, accounts]);

  //ROLE VALIDATION
  useEffect(() => {
    const account = instance.getActiveAccount();
    if (!account) return;

    const roles = (account.idTokenClaims?.roles as string[]) || [];

    const allowed = roles.includes('Provider');

    if (!allowed && !isAccessDenied) {
      navigate('/access-denied', { replace: true });
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    console.log('Authentication check running');
    console.log({ token, userDetails, isError, redirected });

    if (token && !userDetails && !isError) {
      console.log('Refetching user details...');
      refetch();
    }
  }, [token, userDetails, isError, refetch]);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (userDetails && !redirected) {
      console.log('User details ready, redirecting...');
      localStorage.setItem(
        'userId',
        userDetails.userId === null ? '' : String(userDetails.userId)
      );

      localStorage.setItem('username', userDetails.username ?? '');
      setUserDetailsReady(true);
      setRedirected(true);

      // Only navigate to /provider/consults if the user is not already on a different protected route
      if (location.pathname === '/' || location.pathname === '/login') {
        console.log('Redirecting to /provider/consults');
        navigate('/provider/consults', { replace: true });
        history.replaceState(null, '', '/provider/consults');
      }
    }
  }, [userDetails, redirected, navigate, location]);

  if (isError) {
    return (
      <Box className="loading-entra">
        <CustomTypography id="loading-text" variant="h4">
          Error loading user details
        </CustomTypography>
      </Box>
    );
  }

  if (inProgress !== 'none') {
    return (
      <Box className="loading-entra">
        <CustomTypography id="loading-text" variant="h4">
          Loading...
        </CustomTypography>
      </Box>
    );
  }

  if (!isAuthenticated && !isAccessDenied) {
    if (location.pathname !== '/') {
      history.replaceState(null, '', '/');
    }
    return (
      <Box className="container">
        <Box className="center-div">
          <CustomCard id="home-login-card" className="p-32">
            <Box className="logo-qler">
              <QLerLogo />
            </Box>
            {/* <Box>
              <CustomTypography id="login-text" variant="h1" className="pb-32">
                SSO Log in
              </CustomTypography>
            </Box> */}
            <Box>
              <CustomButton
                id="login-button"
                variant="contained"
                color="primary"
                onClick={() => instance.loginRedirect(loginRequest)}
                className="login-microsoft-button"
              >
                Login with SSO
              </CustomButton>
            </Box>
          </CustomCard>
        </Box>
      </Box>
    );
  }

  if (!userDetailsReady) {
    return (
      <Box className="loading-entra">
        <CustomTypography id="loading-text" variant="h4">
          Loading...
        </CustomTypography>
      </Box>
    );
  }

  return <>{children}</>;
}
